import { type BuilderInfo, type UserInfo } from "helpers/AppProvider.types";

import { DiscussionsLinkedTypes, type MediaType, OpenedFrom } from "types/enum";

import { addQsParam } from "utilities/apiHandler";
import { getClientUpdateViewRoute } from "utilities/flatRoutes/clientUpdates.routes";
import { dailyLogViewRoute } from "utilities/flatRoutes/dailyLog.routes";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import {
    type IPreviousLocationState,
    type IRouteLocationState,
} from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { routes } from "utilities/routes";
import { isInIframe } from "utilities/url/url";

import { TemplateType } from "entity/document/Document/Document.api.types";

export const COMMENT_DRAWER_WIDTH_PX = 500;

export interface IDiscussionLinkDetails {
    linkedType: DiscussionsLinkedTypes;
    linkedId: number;
    jobId: number;
    builderId: number;
    userContext?: UserInfo;
    builderInfo?: BuilderInfo | null;
    mediaType?: MediaType;
    documentInstanceId?: number;
    discussionId?: number;
    linkedExternalId?: string;
}

export interface IEntityLink extends Partial<IRouteLocationState> {
    pathname: string;
    /**
     * State from IRouteLocationState, if defined indicates this this is a flat route.
     */
    state?: IPreviousLocationState;
    isRelativeLink: boolean;
}

export function getLinkedEntityDetailPageLink(
    discussionLinkDetails: IDiscussionLinkDetails,
): IEntityLink {
    let detailPageOpener: string | IRouteLocationState;
    let isRelativeLink = true;

    switch (discussionLinkDetails.linkedType) {
        case DiscussionsLinkedTypes.PurchaseOrders:
            detailPageOpener = routes.purchaseOrder.getModalLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.Bills:
        case DiscussionsLinkedTypes.LegacyBills:
            detailPageOpener = routes.bill.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            if (discussionLinkDetails.builderInfo?.flags.billsFullPageConversion) {
                isRelativeLink = false;
            }
            break;
        case DiscussionsLinkedTypes.ToDos:
            detailPageOpener = routes.toDo.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
                true,
                false,
            );
            break;
        case DiscussionsLinkedTypes.ScheduleItems:
            detailPageOpener = routes.schedule.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.Selections:
            detailPageOpener = routes.selection.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.SelectionOptions:
            detailPageOpener = routes.selectionOptions.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            isRelativeLink = false;
            break;
        case DiscussionsLinkedTypes.DailyLogs:
            detailPageOpener = dailyLogViewRoute.link({
                id: discussionLinkDetails.linkedId,
                jobId: discussionLinkDetails.jobId,
            });
            break;
        case DiscussionsLinkedTypes.ChangeOrders:
            detailPageOpener = routes.changeOrder.getChangeOrderDetailsFullPage(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            isRelativeLink = false;
            break;
        case DiscussionsLinkedTypes.OwnerPayments:
            detailPageOpener = routes.ownerInvoice.getDetailsPath(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.Deposit:
            detailPageOpener = routes.deposits.getDetailsPath(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.Bids:
            detailPageOpener = routes.bid.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
                OpenedFrom.Nothing,
                discussionLinkDetails.builderId,
            );
            break;
        case DiscussionsLinkedTypes.Warranty:
            detailPageOpener = routes.warranty.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            if (discussionLinkDetails.builderInfo?.flags.warrantiesModalConversion) {
                isRelativeLink = false;
            }
            break;
        case DiscussionsLinkedTypes.CreditMemos:
            detailPageOpener = routes.creditMemos.getDetailsLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            break;
        case DiscussionsLinkedTypes.Jobsite:
            detailPageOpener = jobDetailRoute.link({ jobId: discussionLinkDetails.linkedId });
            break;
        case DiscussionsLinkedTypes.ClientUpdate:
            detailPageOpener = getClientUpdateViewRoute(discussionLinkDetails.userContext!).link({
                id: discussionLinkDetails.linkedId,
            });
            break;
        case DiscussionsLinkedTypes.Documents:
        case DiscussionsLinkedTypes.PhotoDocs:
        case DiscussionsLinkedTypes.Videos:
            detailPageOpener = routes.media.getMediaPropertiesLink(
                discussionLinkDetails.documentInstanceId ?? 0,
                discussionLinkDetails.mediaType ?? 0,
                discussionLinkDetails.jobId,
                0,
                TemplateType.None,
                0,
                0,
            );
            break;
        case DiscussionsLinkedTypes.Receipts:
            detailPageOpener = routes.receipts.getDetailsLink(discussionLinkDetails.linkedId);
            break;
        case DiscussionsLinkedTypes.Specification:
            detailPageOpener = routes.specification.getOpenConversationLink(
                discussionLinkDetails.linkedId,
                discussionLinkDetails.jobId,
            );
            isRelativeLink = false;
            break;
        case DiscussionsLinkedTypes.Task:
            // These routes embed the external id as a required path segment, so an
            // empty/missing id makes generatePath throw and crashes the render. A
            // comment can legitimately have no resolvable external id (e.g. the
            // linked task/plan page was deleted), so fall through to a blank
            // pathname instead of building an invalid link. Callers treat an
            // empty pathname as "no link" and render unlinked content.
            if (!discussionLinkDetails.linkedExternalId) {
                return { pathname: "", isRelativeLink: true };
            }
            detailPageOpener = routes.task.getDetailsLink(discussionLinkDetails.linkedExternalId);
            isRelativeLink = false;
            break;
        case DiscussionsLinkedTypes.PlanPage:
            if (!discussionLinkDetails.linkedExternalId) {
                return { pathname: "", isRelativeLink: true };
            }
            detailPageOpener = routes.plans.getViewerLink(discussionLinkDetails.linkedExternalId);
            isRelativeLink = false;
            break;
        default:
            return { pathname: "", isRelativeLink: true }; // no resolvable link
    }

    const entityWithCommentsLink: IEntityLink = {
        pathname: "",
        isRelativeLink: isRelativeLink,
    };

    if (typeof detailPageOpener === "string") {
        const urlQS = addQsParam(
            `commentId=${discussionLinkDetails.discussionId ?? 0}`,
            detailPageOpener,
        );
        entityWithCommentsLink.pathname = detailPageOpener + urlQS;
    } else {
        entityWithCommentsLink.pathname = detailPageOpener.pathname;
        entityWithCommentsLink.state = detailPageOpener.state;
    }

    return entityWithCommentsLink;
}

export function isOpenCommentsEnabled(searchParams: URLSearchParams): boolean {
    if (isInIframe()) {
        searchParams = new URLSearchParams(window.top?.location.search);
    }
    return searchParams.has("commentId");
}

export function getCommentIdFromSearchParams(searchParams: URLSearchParams): number | null {
    if (isInIframe()) {
        searchParams = new URLSearchParams(window.top?.location.search);
    }

    const commentId = searchParams.get("commentId");
    if (commentId === null) {
        return null;
    }
    return Number(commentId);
}
